import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  disabledPlaceholder?: string;
  placeholder?: string;
}

/**
 * The chat input bar. Handles text entry + submit via button or Enter key.
 */
const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  disabledPlaceholder = 'Agent is thinking...',
  placeholder = 'Ask something — e.g. "Colorado lakes from 2024"',
}) => {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const isBusy = disabled && disabledPlaceholder === 'Agent is thinking...';

  useEffect(() => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  }, [disabled]);

  const handleSubmit = () => {
    const trimmed = input.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="ves-chat-input">
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={disabled ? disabledPlaceholder : placeholder}
        className="ves-chat-input-field"
      />
      <button
        onClick={handleSubmit}
        disabled={disabled || !input.trim()}
        className="ves-chat-input-button"
      >
        <FontAwesomeIcon
          icon={isBusy ? faSpinner : faPaperPlane}
          spin={isBusy}
        />
      </button>
    </div>
  );
};

export default ChatInput;
